/**
 * Custom hook for managing DevOrganizationSwitcher state and logic
 *
 * Centralizes all state management, API calls, and business logic
 * for the development organization switcher panel.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotify } from '../../../context/NotificationContext';
import { useNotificationCenter } from '../../../context/NotificationCenterContext';
import { useOrganization } from '../../../context/OrganizationContext';

const useDevOrganizationSwitcher = () => {
    // State
    const [isOpen, setIsOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentOrgId, setCurrentOrgId] = useState(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [bypass2FA, setBypass2FA] = useState(false);
    const [activeTab, setActiveTab] = useState('organizations');
    const [roles, setRoles] = useState([]);
    const [currentRoleId, setCurrentRoleId] = useState(null);
    const [emulatingRole, setEmulatingRole] = useState(false);

    // Context hooks
    const authContext = useAuth();
    const notifyContext = useNotify();
    const { getApiUrl } = useOrganization();
    const { refresh: refreshNotifications } = useNotificationCenter();

    const { getUser, getCookie, triggerSessionExpiredModal, apiRequest } = authContext || {};

    // Notification helper
    const showNotification = useCallback((message, type = 'success') => {
        if (notifyContext) {
            if (type === 'success') {
                notifyContext.success(message);
            } else {
                notifyContext.error(message);
            }
        } else {
            console.log(`[${type.toUpperCase()}] ${message}`);
        }
    }, [notifyContext]);

    // Fetch roles from API
    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiRequest('/roles?per_page=100');
            if (response.ok) {
                const data = await response.json();
                const fetchedRoles = data.items || [];

                // Check if Super Admin role exists (role_id = 6)
                const hasSuperAdmin = fetchedRoles.some(role => role.id === 6);

                // If Super Admin doesn't exist in the fetched roles, add it manually
                if (!hasSuperAdmin) {
                    fetchedRoles.push({
                        id: 6,
                        name: 'Super Admin',
                        description: 'Full system access with all permissions'
                    });
                }

                // Sort roles by ID to keep Super Admin at a predictable position
                fetchedRoles.sort((a, b) => a.id - b.id);

                setRoles(fetchedRoles);

                // Get current user's role
                const user = getUser();
                if (user && user.role_id) {
                    setCurrentRoleId(user.role_id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch roles:', error);
        }
    }, [apiRequest, getUser]);

    // Fetch organizations from API
    const fetchOrganizations = useCallback(async () => {
        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
            const response = await fetch(`${baseUrl}/api/dev/organizations`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const data = await response.json();

                setOrganizations([
                    {
                        organization_id: 'local-dev',
                        name: 'Local Dev Database',
                        subdomain: 'local-dev',
                        status: 'development',
                        isLocalDev: true
                    },
                    ...(data.organizations || [])
                ]);
            } else {
                console.warn('Failed to fetch organizations, using fallback data');
                setOrganizations([
                    {
                        organization_id: 'local-dev',
                        name: 'Local Dev Database',
                        subdomain: 'local-dev',
                        status: 'development',
                        isLocalDev: true
                    },
                    {
                        organization_id: 'demo',
                        name: 'Clockwise Demo App',
                        subdomain: 'demo',
                        status: 'active'
                    }
                ]);
            }
        } catch (err) {
            console.error('Error fetching organizations:', err);
            setOrganizations([
                {
                    organization_id: 'local-dev',
                    name: 'Local Dev Database',
                    subdomain: 'local-dev',
                    status: 'development',
                    isLocalDev: true
                },
                {
                    organization_id: 'demo',
                    name: 'Clockwise Demo App',
                    subdomain: 'demo',
                    status: 'active'
                }
            ]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Role emulation
    const emulateRole = useCallback(async (roleId) => {
        try {
            localStorage.setItem('emulated_role_id', roleId);
            setCurrentRoleId(roleId);
            setEmulatingRole(true);
            showNotification(`Now emulating role: ${roles.find(r => r.id === roleId)?.name}`, 'success');

            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (error) {
            showNotification('Failed to emulate role', 'error');
        }
    }, [roles, showNotification]);

    const stopEmulatingRole = useCallback(() => {
        localStorage.removeItem('emulated_role_id');
        setEmulatingRole(false);
        showNotification('Stopped role emulation', 'success');

        setTimeout(() => {
            window.location.reload();
        }, 500);
    }, [showNotification]);

    // Switch organization
    const switchToOrganization = useCallback(async (organization) => {
        try {
            setLoading(true);

            localStorage.setItem('devSwitchTarget', JSON.stringify({
                orgId: organization.organization_id,
                subdomain: organization.subdomain,
                name: organization.name
            }));

            showNotification(`Switching to ${organization.name}...`);

            if (authContext && authContext.logout) {
                await authContext.logout();
            }

            if (organization.isLocalDev) {
                localStorage.removeItem('devOrgContext');
                localStorage.removeItem('devOrgSubdomain');
                localStorage.removeItem('X-Organization-ID');

                showNotification('Switched to local dev database. Please log in with local users.');
            } else if (organization.subdomain === 'main' && organization.organization_id === 'main-prod-db') {
                localStorage.setItem('devOrgContext', 'main-prod-db');
                localStorage.setItem('devOrgSubdomain', 'main');
                localStorage.setItem('X-Organization-ID', 'main');

                showNotification('Switched to main production database. Please log in with production users.');
            } else if (organization.subdomain && organization.subdomain !== 'main') {
                localStorage.setItem('devOrgContext', organization.organization_id);
                localStorage.setItem('devOrgSubdomain', organization.subdomain);
                localStorage.setItem('X-Organization-ID', organization.subdomain);

                showNotification(`Organization context set to ${organization.name}. Please log in with a user from that organization.`);
            } else {
                localStorage.removeItem('devOrgContext');
                localStorage.removeItem('devOrgSubdomain');
                localStorage.removeItem('X-Organization-ID');

                showNotification('Switched to default database. Please log in.');
            }

            window.location.href = '/login';

        } catch (err) {
            showNotification('Failed to switch organization: ' + err.message, 'error');
        } finally {
            setLoading(false);
            setIsOpen(false);
        }
    }, [authContext, showNotification]);

    // 2FA bypass toggle
    const toggle2FABypass = useCallback(() => {
        const newValue = !bypass2FA;
        setBypass2FA(newValue);
        localStorage.setItem('dev2FABypass', newValue.toString());
        localStorage.setItem('BYPASS_2FA', newValue.toString());
        showNotification(
            newValue
                ? '2FA disabled for development'
                : '2FA enabled - you will receive email codes',
            'success'
        );
    }, [bypass2FA, showNotification]);

    // Trigger update banner
    const triggerUpdateBanner = useCallback(async () => {
        try {
            const storageKeys = Object.keys(localStorage);
            storageKeys.forEach(key => {
                if (key.startsWith('updateDismissed_')) {
                    localStorage.removeItem(key);
                }
            });

            const fakeVersion = 'dev-test-' + Date.now();

            const originalFetch = window.fetch;

            window.fetch = async (url, options) => {
                if (url.includes('version.json')) {
                    return {
                        ok: true,
                        json: async () => ({
                            version: fakeVersion,
                            buildTime: new Date().toISOString()
                        })
                    };
                }
                return originalFetch(url, options);
            };

            showNotification('Update banner will appear on page reload');

            sessionStorage.setItem('devFakeBanner', 'true');

            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            showNotification('Failed to trigger banner: ' + error.message, 'error');
        }
    }, [showNotification]);

    // Initialize on mount
    useEffect(() => {
        const DEV = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV === 'true';
        if (DEV) {
            fetchOrganizations();
            fetchRoles();

            const emulatedRoleId = localStorage.getItem('emulated_role_id');
            if (emulatedRoleId) {
                setCurrentRoleId(parseInt(emulatedRoleId));
                setEmulatingRole(true);
            }

            const saved2FABypass = localStorage.getItem('dev2FABypass');
            setBypass2FA(saved2FABypass === 'true');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Set current org ID from localStorage
    useEffect(() => {
        const devOrgId = localStorage.getItem('devOrgContext');
        const orgHeader = localStorage.getItem('X-Organization-ID');

        if (devOrgId === 'main-prod-db' || orgHeader === 'main') {
            setCurrentOrgId('main-prod-db');
        } else if (devOrgId) {
            setCurrentOrgId(devOrgId);
        } else {
            setCurrentOrgId('local-dev');
        }
    }, []);

    // Keyboard shortcut listener
    useEffect(() => {
        const DEV = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV === 'true';
        if (!DEV) return;

        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                setIsVisible(prev => !prev);
                if (!isVisible) {
                    setIsOpen(false);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isVisible]);

    const currentOrg = organizations.find(org => org.organization_id === currentOrgId);

    return {
        // State
        isOpen,
        setIsOpen,
        organizations,
        loading,
        currentOrgId,
        currentOrg,
        isMinimized,
        setIsMinimized,
        showInfo,
        setShowInfo,
        isVisible,
        bypass2FA,
        activeTab,
        setActiveTab,
        roles,
        currentRoleId,
        emulatingRole,

        // Functions
        fetchOrganizations,
        fetchRoles,
        switchToOrganization,
        emulateRole,
        stopEmulatingRole,
        toggle2FABypass,
        triggerUpdateBanner,
        showNotification,

        // Context
        getApiUrl,
        getCookie,
        getUser,
        triggerSessionExpiredModal,
        apiRequest,
        refreshNotifications
    };
};

export default useDevOrganizationSwitcher;
